import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MdAccessTime,
  MdLogin,
  MdLogout,
  MdArrowForward,
  MdCheckCircle,
  MdLocalCafe,
  MdRestaurant,
  MdPauseCircle,
  MdPlayCircle,
  MdTimer,
} from "react-icons/md";
import {
  clockIn,
  clockOut,
  getTodayStatus,
  startBreak,
  endBreak,
} from "services/company/attendanceManagement/attendanceRecords/attendanceRecordsService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import useDesktopActivityTracker from "hooks/useDesktopActivityTracker";

const MyAttendanceCard = ({
  myTodayRecord,
  user,
  onRefresh,
  onStatusUpdate,
  bottomRight = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  // Permissions & Role
  const { hasPermission, permissions } = usePermissions();
  const currentUser = useSelector(selectCurrentUser) || user;
  const userRole =
    getSecureLocalItem("role") ||
    currentUser?.role ||
    currentUser?.role_name ||
    user?.role ||
    "Company";
  const isCompanyRole = userRole?.toLowerCase() === "company";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";

  const canViewAttendanceRecords =
    hasPermission("attendance_records.view") ||
    hasPermission("attendance.view") ||
    (permissions &&
      permissions.some(
        (p) => typeof p === "string" && p.startsWith("attendance"),
      )) ||
    !isEmployeeRole;

  const [actionLoading, setActionLoading] = useState(false);
  const [breakLoading, setBreakLoading] = useState(null);
  const [personalStatus, setPersonalStatus] = useState(null);
  const [elapsedBreakSeconds, setElapsedBreakSeconds] = useState(0);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(300);
  const [liveCurrentTime, setLiveCurrentTime] = useState(() => new Date());
  const autoBreakTriggeredRef = useRef(false);

  useEffect(() => {
    const clockTimer = setInterval(() => {
      setLiveCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(clockTimer);
  }, []);

  const fetchPersonalTodayStatus = useCallback(async () => {
    try {
      const res = await getTodayStatus();
      if (res?.meta?.status === true || res?.status === 200) {
        const data = res.data || res;
        setPersonalStatus(data);
      }
    } catch (err) {
      console.warn("Failed to fetch today status:", err);
    }
  }, []);

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (token && !isCompanyRole) {
      fetchPersonalTodayStatus();
    }
  }, [fetchPersonalTodayStatus, isCompanyRole]);

  const activeBreak = personalStatus?.active_break || null;
  const isOnBreak = Boolean(activeBreak);
  const totalBreakMinutes =
    personalStatus?.break_minutes ?? myTodayRecord?.break_minutes ?? 0;
  const hasActiveTask = Boolean(
    personalStatus?.has_active_task || personalStatus?.active_task,
  );
  const activeTask = personalStatus?.active_task || null;

  useEffect(() => {
    if (!activeBreak?.start_time) {
      setElapsedBreakSeconds(0);
      return;
    }

    const calcElapsed = () => {
      const startMs = new Date(activeBreak.start_time).getTime();
      const nowMs = Date.now();
      const diffSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
      setElapsedBreakSeconds(diffSec);
    };

    calcElapsed();
    const interval = setInterval(calcElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeBreak]);

  const activeRecord = personalStatus?.check_in
    ? personalStatus
    : myTodayRecord || personalStatus;

  const isCheckedIn = Boolean(
    activeRecord?.check_in && !activeRecord?.check_out,
  );
  const isCheckedOut = Boolean(
    activeRecord?.check_in && activeRecord?.check_out,
  );
  const notPunched = !activeRecord?.check_in;

  const trackerState = useDesktopActivityTracker({ enabled: isCheckedIn });

  const isIdleWithoutTask =
    isEmployeeRole && isCheckedIn && !isOnBreak && !hasActiveTask;

  const handleStartBreakRef = useRef();
  useEffect(() => {
    handleStartBreakRef.current = handleStartBreak;
  });

  useEffect(() => {
    if (!isIdleWithoutTask) {
      sessionStorage.removeItem("emp_idle_task_start_time");
      autoBreakTriggeredRef.current = false;
      setIdleSecondsLeft(300);
      return;
    }

    let startTime = sessionStorage.getItem("emp_idle_task_start_time");
    if (!startTime) {
      startTime = Date.now().toString();
      sessionStorage.setItem("emp_idle_task_start_time", startTime);
    }

    const calcIdleRemaining = () => {
      const storedStart =
        sessionStorage.getItem("emp_idle_task_start_time") || startTime;
      const elapsed = Math.floor((Date.now() - Number(storedStart)) / 1000);
      const remaining = Math.max(0, 300 - elapsed);
      setIdleSecondsLeft(remaining);

      if (remaining <= 0 && !autoBreakTriggeredRef.current) {
        autoBreakTriggeredRef.current = true;
        sessionStorage.removeItem("emp_idle_task_start_time");
        if (handleStartBreakRef.current) {
          handleStartBreakRef.current(
            "other_break",
            "Automatic break: No task in progress for 5 minutes",
          );
        }
      }
    };

    calcIdleRemaining();
    const interval = setInterval(calcIdleRemaining, 1000);
    return () => clearInterval(interval);
  }, [isIdleWithoutTask]);

  useEffect(() => {
    const handleTaskChange = () => {
      fetchPersonalTodayStatus();
    };
    window.addEventListener("task_status_changed", handleTaskChange);
    window.addEventListener("focus", handleTaskChange);

    let pollInterval = null;
    if (isCheckedIn) {
      pollInterval = setInterval(fetchPersonalTodayStatus, 15000);
    }

    return () => {
      window.removeEventListener("task_status_changed", handleTaskChange);
      window.removeEventListener("focus", handleTaskChange);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isCheckedIn, fetchPersonalTodayStatus]);

  useEffect(() => {
    if (onStatusUpdate && personalStatus) {
      onStatusUpdate({
        ...personalStatus,
        isIdleWithoutTask,
        idleSecondsLeft,
      });
    }
  }, [personalStatus, isIdleWithoutTask, idleSecondsLeft, onStatusUpdate]);

  if (isCompanyRole) {
    return null;
  }

  const formatTime = (dateStr) => {
    if (!dateStr) return "--:--";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimerString = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(remMins).padStart(
        2,
        "0",
      )}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(remMins).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0",
    )}`;
  };

  const getCoordinates = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: 0, longitude: 0 });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => {
          resolve({ latitude: 0, longitude: 0 });
        },
        { timeout: 5000 },
      );
    });
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockIn(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked in successfully!");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-in failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-in error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockOut(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(
          response?.meta?.message || "Checked out successfully!",
        );
        sessionStorage.removeItem("emp_idle_task_start_time");
        window.dispatchEvent(new Event("task_status_changed"));
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        const errorMsg = response?.meta?.message || "Check-out failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-out error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async (breakType, customRemark) => {
    if (!isCheckedIn) {
      showErrorToast("Please check in first before taking a break.");
      return;
    }
    if (isOnBreak) {
      showErrorToast(
        "You already have an active break in progress. End it first.",
      );
      return;
    }

    setBreakLoading(breakType);
    try {
      const breakLabel = breakType.replace("_", " ").toUpperCase();
      const response = await startBreak({
        break_type: breakType,
        remarks: customRemark || `Started ${breakLabel} from dashboard`,
      });
      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(
          response?.meta?.message || `${breakLabel} started! Enjoy your break.`,
        );
        sessionStorage.removeItem("emp_idle_task_start_time");
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        showErrorToast(response?.meta?.message || "Failed to start break");
      }
    } catch (err) {
      console.error("Start break error:", err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to start break",
      );
    } finally {
      setBreakLoading(null);
    }
  };

  const handleEndBreak = async () => {
    setBreakLoading("end");
    try {
      const response = await endBreak();
      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(
          response?.meta?.message || "Break ended! Welcome back to work.",
        );
        await fetchPersonalTodayStatus();
        if (onRefresh) onRefresh();
      } else {
        showErrorToast(response?.meta?.message || "Failed to end break");
      }
    } catch (err) {
      console.error("End break error:", err);
      showErrorToast(
        err?.meta?.message || err?.message || "Failed to end break",
      );
    } finally {
      setBreakLoading(null);
    }
  };

  const breakButtonsConfig = [
    {
      id: "tea_break",
      label: "Tea Break",
      icon: <MdLocalCafe size={18} />,
      activeColor: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      lightBg: "#fef3c7",
    },
    {
      id: "lunch_break",
      label: "Lunch Break",
      icon: <MdRestaurant size={18} />,
      activeColor: "#f97316",
      gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      lightBg: "#ffedd5",
    },
    {
      id: "other_break",
      label: "Other Break",
      icon: <MdPauseCircle size={18} />,
      activeColor: "#6366f1",
      gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      lightBg: "#e0e7ff",
    },
  ];

  const getStatusChipConfig = () => {
    if (isOnBreak) {
      const label =
        activeBreak?.break_type === "tea_break"
          ? "On Tea Break"
          : activeBreak?.break_type === "lunch_break"
          ? "On Lunch Break"
          : "On Break";
      return {
        label,
        bg: isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
        color: "#d97706",
      };
    }
    if (isCheckedIn) {
      return {
        label: "Checked In",
        bg: isDark ? alpha("#10b981", 0.2) : "#d1fae5",
        color: "#059669",
      };
    }
    if (isCheckedOut) {
      return {
        label: "Shift Ended",
        bg: isDark ? alpha("#6366f1", 0.2) : "#e0e7ff",
        color: "#4338ca",
      };
    }
    return {
      label: "Not Checked In",
      bg: isDark ? alpha("#f43f5e", 0.2) : "#ffe4e6",
      color: "#e11d48",
    };
  };

  const formatLiveClock = () => {
    return liveCurrentTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getTimezoneString = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const offsetMinutes = -new Date().getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? "+" : "-";
      const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(
        2,
        "0",
      );
      const mins = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");
      return `${tz} (UTC ${sign}${hours}:${mins})`;
    } catch {
      return "Local Timezone";
    }
  };

  const calculateActiveHoursShort = () => {
    if (!activeRecord?.check_in) return "00h 00m";
    if (
      activeRecord?.working_minutes !== undefined &&
      activeRecord?.working_minutes !== null &&
      activeRecord?.working_minutes > 0
    ) {
      const hrs = Math.floor(activeRecord.working_minutes / 60);
      const mins = activeRecord.working_minutes % 60;
      return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(
        2,
        "0",
      )}m`;
    }
    const startTime = new Date(activeRecord.check_in).getTime();
    const endTime = activeRecord.check_out
      ? new Date(activeRecord.check_out).getTime()
      : Date.now();
    const diffMs = Math.max(0, endTime - startTime);
    const totalMins = Math.floor(diffMs / (1000 * 60));
    const netMins = Math.max(0, totalMins - (totalBreakMinutes || 0));
    const hrs = Math.floor(netMins / 60);
    const mins = netMins % 60;
    return `${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
  };

  const statusChip = getStatusChipConfig();

  const cardPaperSx = {
    p: { xs: 1.25, sm: 1.5 },
    borderRadius: "8px",
    bgcolor: isDark ? "#1e293b" : "#f0fdf4",
    border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.12)" : "#bbf7d0"}`,
    boxShadow: isDark
      ? "0 1px 3px rgba(0,0,0,0.25)"
      : "0 1px 3px rgba(15,23,42,0.03)",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1.1fr) minmax(0, 1fr)",
        },
        gridTemplateRows: "auto 1fr",
        gap: 2,
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {/* ============ CARD 1: Time & Attendance ============ */}
      <Paper
        elevation={0}
        sx={{ ...cardPaperSx, gridRow: { md: "1 / span 2" } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* Header Title & Live Status Chip */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              pb: 1.5,
              borderBottom: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"
              }`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "4px",
                  bgcolor: isDark ? alpha("#4f46e5", 0.2) : "#eef2ff",
                  color: "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdAccessTime size={16} />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                }}
              >
                Time & Attendance
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {trackerState.isConnected && (
                <Tooltip
                  title={`Tracker: ${
                    trackerState.isActive
                      ? "Active"
                      : `Idle (${trackerState.idleSeconds}s)`
                  }`}
                >
                  <Chip
                    size="small"
                    label={
                      trackerState.isActive
                        ? "Active"
                        : `Idle (${trackerState.idleSeconds}s)`
                    }
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      height: 20,
                      borderRadius: "4px",
                      bgcolor: trackerState.isActive
                        ? isDark
                          ? alpha("#10b981", 0.2)
                          : "#ecfdf5"
                        : isDark
                        ? alpha("#f43f5e", 0.2)
                        : "#ffe4e6",
                      color: trackerState.isActive ? "#059669" : "#e11d48",
                    }}
                  />
                </Tooltip>
              )}
              <Box
                component="span"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: "4px",
                  bgcolor: statusChip.bg,
                  color: statusChip.color,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: statusChip.color,
                  }}
                />
                {statusChip.label}
              </Box>
            </Stack>
          </Box>

          {/* Station Time & 3 Metrics */}
          <Box
            sx={{
              py: 1.5,
              px: 1.5,
              mb: 1.5,
              borderRadius: "6px",
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
              border: `1px solid ${
                isDark ? "rgba(255, 255, 255, 0.54)" : "#042341"
              }`,
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                letterSpacing: "1px",
                textTransform: "uppercase",
                fontSize: "0.75rem",
              }}
            >
              Station Standard Time
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontFamily: "monospace",
                fontSize: { xs: "1.65rem", sm: "1.85rem" }, // Increased font size
                my: 0.5,
              }}
            >
              {formatLiveClock()}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.8rem" }} // Increased from 0.7rem
            >
              {getTimezoneString()}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${
                  isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                }`,
              }}
            >
              <Box
                sx={{
                  p: 1.25, // Slightly more padding
                  borderRadius: "4px",
                  bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: `1px solid ${
                    isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                  }`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    fontSize: "0.7rem", // Increased from 0.6rem
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  Check-In
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem", // Increased from 0.8rem
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {formatTime(activeRecord?.check_in)}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: "4px",
                  bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: `1px solid ${
                    isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                  }`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    fontSize: "0.7rem", // Increased from 0.6rem
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  Check-Out
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem", // Increased from 0.8rem
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {formatTime(activeRecord?.check_out)}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: "4px",
                  bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  border: `1px solid ${
                    isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                  }`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    fontSize: "0.7rem", // Increased from 0.6rem
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  Active Hours
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem", // Increased from 0.8rem
                    color: "#2563eb",
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {calculateActiveHoursShort()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Active Break Live Banner */}
          <AnimatePresence>
            {isOnBreak && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1,
                    bgcolor: isDark ? alpha("#f59e0b", 0.15) : "#fef3c7",
                    border: 1,
                    borderColor: isDark ? alpha("#f59e0b", 0.3) : "#fde68a",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#f59e0b",
                          animation: "pulse 1.5s infinite ease-in-out",
                          "@keyframes pulse": {
                            "0%": { opacity: 0.4 },
                            "50%": { opacity: 1 },
                            "100%": { opacity: 0.4 },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: isDark ? "#fbbf24" : "#92400e",
                        }}
                      >
                        {activeBreak?.break_type === "tea_break"
                          ? "On Tea Break"
                          : activeBreak?.break_type === "lunch_break"
                          ? "On Lunch Break"
                          : "On Other Break"}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <MdTimer size={14} color="#d97706" />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          fontFamily: "monospace",
                          color: "#d97706",
                        }}
                      >
                        {formatTimerString(elapsedBreakSeconds)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    disabled={breakLoading === "end"}
                    onClick={handleEndBreak}
                    startIcon={
                      breakLoading === "end" ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <MdPlayCircle size={16} />
                      )
                    }
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: 800,
                      py: 0.8,
                      fontSize: "0.75rem",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#ffffff",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      },
                    }}
                  >
                    {breakLoading === "end"
                      ? "Ending..."
                      : "End Break & Resume"}
                  </Button>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Idle Warning Banner */}
          <AnimatePresence>
            {isIdleWithoutTask && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 1,
                    bgcolor: isDark ? alpha("#ef4444", 0.12) : "#fff1f2",
                    border: "1px solid",
                    borderColor: isDark ? alpha("#ef4444", 0.45) : "#fecdd3",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? "#fca5a5" : "#9f1239",
                      }}
                    >
                      No task in progress
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.2,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha("#ef4444", 0.25) : "#ffe4e6",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <MdTimer size={12} color="#e11d48" />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 900,
                          fontFamily: "monospace",
                          color: "#e11d48",
                        }}
                      >
                        {formatTimerString(idleSecondsLeft)}
                      </Typography>
                    </Box>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      100,
                      Math.max(0, (idleSecondsLeft / 300) * 100),
                    )}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: isDark ? alpha("#ef4444", 0.2) : "#fecdd3",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 2,
                        bgcolor: idleSecondsLeft < 60 ? "#ef4444" : "#f59e0b",
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => navigate("/company/tasks")}
                    endIcon={<MdArrowForward size={12} />}
                    sx={{
                      py: 0.4,
                      borderRadius: 1,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      borderColor: "#f43f5e",
                      color: "#e11d48",
                      "&:hover": { bgcolor: "#ffe4e6" },
                    }}
                  >
                    Pick a Task
                  </Button>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Active Task Badge */}
          <AnimatePresence>
            {!isOnBreak && hasActiveTask && activeTask && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    mb: 1.5,
                    borderRadius: 1,
                    bgcolor: isDark ? alpha("#10b981", 0.12) : "#f0fdf4",
                    border: 1,
                    borderColor: isDark ? alpha("#10b981", 0.3) : "#bbf7d0",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#059669",
                          display: "block",
                          fontSize: "0.65rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Active Task
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeTask.title}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => navigate("/company/tasks")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "#059669",
                        p: 0,
                      }}
                    >
                      View
                    </Button>
                  </Stack>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions (Check In / Out) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              mt: 0,
              flex: "0 0 auto", // Ensure it stays anchored and keeps its height
            }}
          >
            {notPunched && (
              <Button
                variant="contained"
                fullWidth
                disabled={actionLoading}
                onClick={handleCheckIn}
                startIcon={
                  actionLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MdLogin />
                  )
                }
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 800,
                  py: 2, // Increased padding to make button larger
                  fontSize: "0.95rem",
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#ffffff",
                }}
              >
                {actionLoading ? "Punching In..." : "Check In Now"}
              </Button>
            )}

            {isCheckedIn && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={actionLoading || isOnBreak}
                  onClick={handleCheckOut}
                  startIcon={
                    actionLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <MdLogout size={16} />
                    )
                  }
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    py: 2, // Increased padding to make button larger
                    fontSize: "0.95rem",
                    bgcolor: "#dc2626",
                    color: "#ffffff",
                    "&:hover": { bgcolor: "#b91c1c" },
                  }}
                >
                  {actionLoading
                    ? "Punching Out..."
                    : isOnBreak
                    ? "End Break First"
                    : "Clock Out"}
                </Button>
              </Box>
            )}

            {isCheckedOut && (
              <Button
                variant="outlined"
                fullWidth
                disabled
                startIcon={<MdCheckCircle color="#6366f1" />}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 2,
                  fontSize: "0.95rem",
                  borderColor: isDark ? "rgba(99, 102, 241, 0.3)" : "#c7d2fe",
                  color: "primary.main",
                }}
              >
                Shift Completed
              </Button>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                mt: 1,
              }}
            >
              <MdCheckCircle size={12} color="#059669" />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.65rem" }}
              >
                Verified via Office Network
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ============ CARD 2: Break Options ============ */}
      {isEmployeeRole && (
        <Paper
          elevation={0}
          sx={{ ...cardPaperSx, gridColumn: { md: "2" }, gridRow: { md: "1" } }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
                pb: 1.5,
                borderBottom: `1px solid ${
                  isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"
                }`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 24,
                    height: 24,

                    color: "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MdLocalCafe size={16} />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    fontSize: "1rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Break Options
                </Typography>
              </Stack>
              {!isCheckedIn && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", fontSize: "0.65rem" }}
                >
                  (Punch in)
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1,
                flex: 1,
              }}
            >
              {breakButtonsConfig.map((item) => {
                const isThisBreakActive = activeBreak?.break_type === item.id;
                const isButtonDisabled =
                  !isCheckedIn ||
                  isCheckedOut ||
                  (isOnBreak && !isThisBreakActive) ||
                  Boolean(breakLoading);
                const isLoadingThis = breakLoading === item.id;

                return (
                  <Tooltip
                    key={item.id}
                    title={
                      !isCheckedIn
                        ? "Punch in first"
                        : isCheckedOut
                        ? "Shift completed"
                        : isOnBreak && !isThisBreakActive
                        ? "End active break first"
                        : ""
                    }
                    arrow
                  >
                    <Box sx={{ width: "100%", height: "100%" }}>
                      <Button
                        variant={isThisBreakActive ? "contained" : "outlined"}
                        fullWidth
                        disabled={isButtonDisabled}
                        onClick={() =>
                          isThisBreakActive
                            ? handleEndBreak()
                            : handleStartBreak(item.id)
                        }
                        sx={{
                          width: "100%",
                          height: "100%",
                          py: 1.5,
                          px: 0.5,
                          borderRadius: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          borderColor: isThisBreakActive
                            ? item.activeColor
                            : isDark
                            ? alpha(item.activeColor, 0.3)
                            : alpha(item.activeColor, 0.4),
                          color: isThisBreakActive
                            ? "#ffffff"
                            : isDark
                            ? alpha(item.activeColor, 0.95)
                            : item.activeColor,
                          background: isThisBreakActive
                            ? item.gradient
                            : isDark
                            ? alpha(item.activeColor, 0.08)
                            : item.lightBg,
                          boxShadow: isThisBreakActive
                            ? `0 4px 10px ${alpha(item.activeColor, 0.3)}`
                            : "none",
                          "&:hover": {
                            borderColor: item.activeColor,
                            background: isThisBreakActive
                              ? item.gradient
                              : alpha(item.activeColor, 0.15),
                          },
                          "&.Mui-disabled": { opacity: 0.55 },
                        }}
                      >
                        {isLoadingThis ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: item.activeColor, my: 0.2 }}
                          />
                        ) : (
                          item.icon
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            lineHeight: 1.1,
                            textAlign: "center",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Button>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}

      {/* ============ CARD 3: Slot below Break Options ============ */}
      {isEmployeeRole && bottomRight && (
        <Box
          sx={{
            minWidth: 0,
            gridColumn: { xs: "1", md: "2" },
            gridRow: { md: "2" },
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {bottomRight}
        </Box>
      )}
    </Box>
  );
};

export default MyAttendanceCard;
